import { useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { ApiError } from '@/lib/api'
import * as ontology from '@intelhub/shared/ontology'
import {
  useUserPreferences,
  useUpdateUserPreferences,
  useSavedSearches,
  useDeleteSavedSearch,
  useUserTimezone,
  useUpdateUserTimezone,
} from '@/hooks/queries'
import type { Severity } from '@/lib/types'
import { X } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'

// Ontology-driven options: display names for UI, slugs for storage
const REGION_OPTIONS = ontology.regions
  .filter((e) => !e.parentSlug)
  .map((e) => ({ name: e.name, slug: e.slug }))
const INDUSTRY_OPTIONS = ontology.industries.map((e) => ({ name: e.name, slug: e.slug }))
const REGIONS = REGION_OPTIONS.map((o) => o.name)
const INDUSTRIES = INDUSTRY_OPTIONS.map((o) => o.name)

const SEVERITIES: Severity[] = ontology.severity
  .filter((e) => e.slug !== 'informational')
  .map((e) => e.slug as Severity)

function slugToRegionName(slug: string): string {
  return REGION_OPTIONS.find((o) => o.slug === slug)?.name ?? slug
}
function slugToIndustryName(slug: string): string {
  return INDUSTRY_OPTIONS.find((o) => o.slug === slug)?.name ?? slug
}
function regionNameToSlug(name: string): string {
  return REGION_OPTIONS.find((o) => o.name === name)?.slug ?? name
}
function industryNameToSlug(name: string): string {
  return INDUSTRY_OPTIONS.find((o) => o.name === name)?.slug ?? name
}

function MultiCheckbox({
  label,
  options,
  selected,
  onChange,
}: {
  label: string
  options: string[]
  selected: string[]
  onChange: (vals: string[]) => void
}) {
  function toggle(val: string) {
    onChange(
      selected.includes(val) ? selected.filter((v) => v !== val) : [...selected, val],
    )
  }

  return (
    <div>
      <p className="text-sm font-medium mb-2">{label}</p>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => toggle(opt)}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
              selected.includes(opt)
                ? 'bg-primary text-primary-foreground border-primary'
                : 'border-border bg-background text-foreground hover:bg-accent'
            }`}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  )
}

/** The browser's resolved IANA timezone, or null if the runtime will not say. */
function browserTimezone(): string | null {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone
    return typeof zone === 'string' && zone.length > 0 ? zone : null
  } catch {
    return null
  }
}

/** Suggestions for the timezone field. The server decides what is valid. */
function timezoneOptions(): string[] {
  const supported =
    typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : []
  return supported.includes('UTC') ? supported : ['UTC', ...supported]
}

/**
 * KAN-939 — the reader's timezone. The briefing scheduler sends at about
 * 05:30 in the stored zone (`users.timezone`, default UTC).
 *
 * The browser's own zone is offered as a suggestion only: using it fills the
 * field, and nothing is stored until the reader presses Save timezone.
 */
function TimezoneSetting({ userId }: { userId: string }) {
  const { data } = useUserTimezone(userId)
  const updateTimezone = useUpdateUserTimezone()
  const saved = data?.timezone ?? null
  // null = the reader has not edited the field; show the saved value.
  const [draft, setDraft] = useState<string | null>(null)
  const value = draft ?? saved ?? ''
  const dirty = draft !== null && draft.trim() !== '' && draft.trim() !== saved
  const suggested = useMemo(browserTimezone, [])
  const options = useMemo(timezoneOptions, [])

  const refused =
    updateTimezone.error instanceof ApiError && updateTimezone.error.status === 400

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold">Briefing time</h3>
      <p className="text-sm text-muted-foreground">
        Your briefing arrives at about 05:30 local time, based on the timezone saved here.
      </p>
      <div className="space-y-2">
        <Label htmlFor="profile-timezone">Timezone</Label>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            id="profile-timezone"
            list="profile-timezone-options"
            autoComplete="off"
            spellCheck={false}
            placeholder="For example, Europe/London"
            className="max-w-xs"
            value={value}
            onChange={(e) => setDraft(e.target.value)}
          />
          <datalist id="profile-timezone-options">
            {options.map((zone) => (
              <option key={zone} value={zone} />
            ))}
          </datalist>
          <Button
            type="button"
            size="sm"
            disabled={!dirty || updateTimezone.isPending}
            onClick={() =>
              updateTimezone.mutate(
                { userId, timezone: draft!.trim() },
                // Drop the draft: the field falls back to the saved value,
                // which the hook has set to the server's canonical spelling.
                { onSuccess: () => setDraft(null) },
              )
            }
          >
            {updateTimezone.isPending ? 'Saving…' : 'Save timezone'}
          </Button>
        </div>
        {suggested && suggested !== value && (
          <p className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span>Your browser is set to {suggested}.</span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => setDraft(suggested)}
            >
              Use {suggested}
            </Button>
          </p>
        )}
        <div aria-live="polite">
          {updateTimezone.isError ? (
            <p role="alert" className="text-xs text-destructive">
              {refused
                ? "That isn't a timezone we recognise. Pick one from the list, such as Europe/London."
                : "Your timezone wasn't saved. Try again."}
            </p>
          ) : updateTimezone.isSuccess && !dirty ? (
            <p className="text-xs text-muted-foreground">Saved.</p>
          ) : null}
        </div>
      </div>
    </div>
  )
}

/**
 * Profile section — preferences that persist via `PUT /preferences/:id`, plus
 * the reader's timezone via `PUT /preferences/:id/timezone` (KAN-939).
 *
 * Controls rendered here are limited to fields the API accepts today: default
 * regions, default industries, severity threshold, timezone, and the
 * saved-search list. Fields that are not persisted by the current backend
 * (display name, keyword watches, tracked-vendor toggles) are intentionally
 * not rendered — dead UI was removed in the surface-audit cheap-wins pass.
 * When the API surface for those grows, add the controls back alongside a new
 * mutation hook, not as local state.
 */
export function ProfileSection() {
  const { user } = useAuth()
  const userId = user?.sub ?? ''
  const { data: preferences } = useUserPreferences(userId)
  const updatePreferences = useUpdateUserPreferences()
  const { data: savedSearches = [] } = useSavedSearches(userId)
  const deleteSavedSearch = useDeleteSavedSearch()

  const prefs = preferences ?? {
    defaultRegions: [] as string[],
    defaultIndustries: [] as string[],
    defaultContentTypes: [] as string[],
    severityThreshold: null,
    criticalAlertsEnabled: true,
    emailEnabled: false,
    trackedVendors: [] as string[],
    keywordWatches: [] as { id: string; keyword: string; boostWeight: number | null }[],
  }

  return (
    <div className="flex flex-col gap-5">
      {/* Default filters */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold">Default filters</h3>
        <MultiCheckbox
          label="Default regions"
          options={REGIONS}
          selected={(prefs.defaultRegions ?? []).map(slugToRegionName)}
          onChange={(names) =>
            updatePreferences.mutate({
              userId,
              defaultRegions: names.map(regionNameToSlug),
            })
          }
        />
        <MultiCheckbox
          label="Default industries"
          options={INDUSTRIES}
          selected={(prefs.defaultIndustries ?? []).map(slugToIndustryName)}
          onChange={(names) =>
            updatePreferences.mutate({
              userId,
              defaultIndustries: names.map(industryNameToSlug),
            })
          }
        />
        <div>
          <p className="text-sm font-medium mb-2">Severity threshold</p>
          <div className="flex flex-wrap gap-2">
            {SEVERITIES.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() =>
                  updatePreferences.mutate({ userId, severityThreshold: s })
                }
                className={`rounded-full border px-3 py-1 text-xs font-medium capitalize transition-colors ${
                  prefs.severityThreshold === s
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'border-border bg-background hover:bg-accent'
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      </div>

      <Separator />

      <TimezoneSetting userId={userId} />

      <Separator />

      {/* Saved searches */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold">Saved searches</h3>
        {savedSearches.length === 0 ? (
          <p className="text-sm text-muted-foreground">No saved searches.</p>
        ) : (
          <div className="space-y-2">
            {savedSearches.map((ss) => (
              <div
                key={ss.id}
                className="flex items-center justify-between rounded-md border bg-card px-3 py-2"
              >
                <span className="text-sm font-medium">{ss.name}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-destructive"
                  onClick={() => deleteSavedSearch.mutate({ userId, id: ss.id })}
                  // R28: the user's own saved-search name, in an attribute.
                  data-ph-no-capture
                  aria-label={`Delete saved search ${ss.name}`}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
